import { createStore } from "vuex";
import config from "../config";
import axios from "axios";
import { loadProfile, savedPVC, forgetPVC, publicActivity, financialAccess } from '../services/privacy';

let profileController;
let profileRequest = 0;
function accountHeaders(state, login) {
    const pvc = state.account?.miner === login && state.account.privateView ? savedPVC(login) : '';
    return pvc ? { 'X-Molepool-PVC': pvc } : {};
}

const coin = config.coin.toLowerCase()

export default createStore({
    state: {
        status: "pending",
        error: "",
        accountEpoch: 0,
        accountLogin: '',
        accountViewError: '',
        accountRetryAfter: 0,
        accountRetryUntil: 0,
        newBlock: 0,
        home: {
            algo: "",
            scheme: "",
            reward: 0,
            fee: 0,
            totalMiners: 0,
            totalHashrate: 0,
            currentEffort: 0,
            lastBlockFound: 0,
            newBlocks: 0,
            totalBlocks: 0,
            networkHashrate: 0,
            networkDifficulty: 0,
            currentHeight: 0,
            btc: "",
            btc_24h_change: "",
            usd: "",
            usd_24h_change: "",
            chart: [],
        },
        blocks: {
            luck: {},
            candidate: [],
            blocks: [],
        },
        miners: {
            miners: [],
            totalCount: 0,
        },
        account: {
            currentHashrate: 0,
            averageHashrate: 0,
            lastBlockFound: 0,
            blocksFound: 0,
            personalShares: 0,
            personalEffort: 0,
            balance: 0,
            pending: 0,
            immature: 0,
            daysReward: 0,
            workerTotal: 0,
            workerOnline: 0,
            workerOffline: 0,
            chart: [],
            bestShare: {
                time: 0,
                share: 0,
                netDiff: 0,
                port: 0,
            },
            workers: []
        },
        account_rewards: {
            luck: {},
            sumrewards: [],
            rewards: []
        },
        account_payments: [],
        group: {
            groupID: "", 
            shares: [],
        }
    },
    getters: {
        getStatus: (state) => state.status,
        getNewBlocks: (state) => state.newBlock,
        getHome: (state) => state.home,
        getBlocks: (state) => state.blocks,
        getMiners: (state) => state.miners,
        getAccount: (state) => state.account,
        getAccountRewards: (state) => state.account_rewards,
        getAccountPayments: (state) => state.account_payments,
        getGroup: (state) => state.group,
    },
    actions: {
        async fetchNewBlock({ commit }) {
            try {
                const data = await axios.get(`/api/v1/newBlock`);
                commit("SET_NEW_BLOCKS", data.data);
            } catch (error) {
                commit("SET_ERROR", error);
            }
        },
        async fetchHome({ commit }) {
            try {
                const data = await axios.get(`/api/v1/stats`);
                commit("SET_HOME", data.data.result);
                if (data.data.status) {
                    commit("SET_STATUS", "success");
                } else {
                    commit("SET_STATUS", "pending");
                }
            } catch (error) {
                commit("SET_STATUS", "error");
                commit("SET_ERROR", error);
            }
        },
        async fetchBlocks({ commit }) {
            try {
                const data = await axios.get(`/api/v1/blocks`);
                commit("SET_BLOCKS", data.data.result);
                if (data.data.status) {
                    commit("SET_STATUS", "success");
                } else {
                    commit("SET_STATUS", "pending");
                }
            } catch (error) {
                commit("SET_STATUS", "error");
                commit("SET_ERROR", error);
            }
        },
        async fetchMiners({ commit }, params) {
            try {
                const data = await axios.get(`/api/v1/miners/${params.page}/${params.size}`
                );
                commit("SET_MINERS", data.data.result);
                if (data.data.status) {
                    commit("SET_STATUS", "success");
                } else {
                    commit("SET_STATUS", "pending");
                }
            } catch (error) {
                commit("SET_STATUS", "error");
                commit("SET_ERROR", error);
            }
        },
        async fetchAccount({ commit, state }, params) {
            if (Date.now() < state.accountRetryUntil) return;
            const epoch = state.accountEpoch;
            const request = ++profileRequest;
            profileController?.abort();
            profileController = new AbortController();
            try {
                const result = await loadProfile(params.login, { page: params.page, size: params.size, signal: profileController.signal });
                if (epoch !== state.accountEpoch || request !== profileRequest) return;
                commit('SET_ACCOUNT', result.account);
                commit('SET_ACCOUNT_VIEW_ERROR', { error: result.accessError, retryAfter: result.retryAfter });
                if (!financialAccess(result.account, params.login)) commit('CLEAR_ACCOUNT_FINANCES');
                commit('SET_STATUS', 'success');
            } catch (error) {
                if (error.name === 'AbortError' || epoch !== state.accountEpoch || request !== profileRequest) return;
                commit('REVOKE_PRIVATE_VIEW');
                commit('SET_ACCOUNT_VIEW_ERROR', { error: error.message, retryAfter: error.retryAfter });
            }
        },
        async fetchAccountRewards({ commit, state }, params) {
            if (!financialAccess(state.account, params.login)) return;
            const epoch = state.accountEpoch;
            try {
                const data = await axios.get(`/api/v1/account/${encodeURIComponent(params.login)}/rewards`, { headers: accountHeaders(state, params.login) });
                if (epoch !== state.accountEpoch || !financialAccess(state.account, params.login)) return;
                commit("SET_ACCOUNT_REWARDS", data.data.result);
                if (data.data.status) {
                    commit("SET_STATUS", "success");
                } else {
                    commit("SET_STATUS", "pending");
                }
            } catch (error) {
                if (epoch !== state.accountEpoch) return;
                if ([401, 403, 404, 429].includes(error.response?.status)) {
                    if ([401, 404].includes(error.response.status)) forgetPVC(params.login);
                    commit('REVOKE_PRIVATE_VIEW');
                }
                commit("SET_STATUS", "error");
                commit("SET_ERROR", 'api_unavailable');
            }
        },
        async fetchAccountPayments({ commit, state }, params) {
            if (!financialAccess(state.account, params.login)) return;
            const epoch = state.accountEpoch;
            try {
                const data = await axios.get(`/api/v1/account/${encodeURIComponent(params.login)}/payments`, { headers: accountHeaders(state, params.login) });
                if (epoch !== state.accountEpoch || !financialAccess(state.account, params.login)) return;
                commit("SET_ACCOUNT_PAYMENTS", data.data.result);
                if (data.data.status) {
                    commit("SET_STATUS", "success");
                } else {
                    commit("SET_STATUS", "pending");
                }
            } catch (error) {
                if (epoch !== state.accountEpoch) return;
                if ([401, 403, 404, 429].includes(error.response?.status)) {
                    if ([401, 404].includes(error.response.status)) forgetPVC(params.login);
                    commit('REVOKE_PRIVATE_VIEW');
                }
                commit("SET_STATUS", "error");
                commit("SET_ERROR", 'api_unavailable');
            }
        },
        async fetchGroup({ commit }, params) {
            try {
                const data = await axios.get(`/api/v1/group/${params.group}`);
                commit("SET_GROUP", data.data.result);
                if (data.data.status) {
                    commit("SET_STATUS", "success");
                } else {
                    commit("SET_STATUS", "pending");
                }
            } catch (error) {
                commit("SET_STATUS", "error");
                commit("SET_ERROR", error);
            }
        },
    },
    mutations: {
        BEGIN_ACCOUNT(state, login) {
            profileController?.abort();
            state.accountEpoch++;
            state.accountLogin = login;
            state.account = { workers: [], chart: [] };
            state.account_rewards = { luck: {}, sumrewards: [], rewards: [] };
            state.account_payments = [];
            state.accountViewError = '';
            state.accountRetryAfter = 0;
            state.accountRetryUntil = 0;
        },
        REVOKE_PRIVATE_VIEW(state) {
            profileController?.abort();
            state.accountEpoch++;
            if (state.account.identityType === 'mp') state.account = publicActivity(state.account);
            state.account_rewards = { luck: {}, sumrewards: [], rewards: [] };
            state.account_payments = [];
        },
        OPEN_PRIVATE_VIEW(state, account) {
            profileController?.abort();
            state.accountEpoch++;
            state.account = account;
            state.accountViewError = '';
            state.accountRetryAfter = 0;
            state.accountRetryUntil = 0;
        },
        CLEAR_ACCOUNT_FINANCES(state) {
            state.account_rewards = { luck: {}, sumrewards: [], rewards: [] };
            state.account_payments = [];
        },
        SET_ACCOUNT_VIEW_ERROR(state, { error = '', retryAfter = 0 }) {
            state.accountViewError = error;
            state.accountRetryAfter = retryAfter;
            state.accountRetryUntil = retryAfter ? Date.now() + retryAfter * 1000 : 0;
        },
        SET_STATUS(state, status) {
            state.status = status;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_NEW_BLOCKS(state, newBlock) {
            state.newBlock = newBlock;
        },
        SET_HOME(state, home) {
            state.home = home;
        },
        SET_BLOCKS(state, blocks) {
            state.blocks = blocks;
        },
        SET_MINERS(state, miners) {
            state.miners = miners;
        },
        SET_ACCOUNT(state, account) {
            state.account = account;
        },
        SET_ACCOUNT_REWARDS(state, account_rewards) {
            state.account_rewards = account_rewards;
        },
        SET_ACCOUNT_PAYMENTS(state, account_payments) {
            state.account_payments = account_payments;
        },
        SET_GROUP(state, group) {
            state.group = group;
        },
    },
});
